import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Save, CheckCircle2, Copy, ChevronDown, ChevronUp, Plus, Trash2, Eye, Pencil,
    Type, AlignLeft, Mail, Phone, Calendar as CalIcon, Cake, Hash, ToggleLeft, ListChecks, Circle,
    ChevronsUpDown, PenTool, Fingerprint, Clock, GripVertical, Info, AlertTriangle,
    Archive, RotateCcw, GitBranch, MoreVertical,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { CATEGORIES, CAT_LABEL } from "./Forms";

/* ==========================================================================
 * Field kind catalogue
 * ======================================================================= */
const KINDS = [
    { kind: "section_heading",   label: "Section heading",   icon: Type,           group: "layout" },
    { kind: "informational_text",label: "Informational text",icon: Info,           group: "layout" },
    { kind: "short_text",        label: "Short text",        icon: Type,           group: "input" },
    { kind: "long_text",         label: "Long text",         icon: AlignLeft,      group: "input" },
    { kind: "email",             label: "Email address",     icon: Mail,           group: "input" },
    { kind: "phone",             label: "Telephone number",  icon: Phone,          group: "input" },
    { kind: "date",              label: "Date",              icon: CalIcon,        group: "input" },
    { kind: "date_of_birth",     label: "Date of birth",     icon: Cake,           group: "input" },
    { kind: "number",            label: "Number",            icon: Hash,           group: "input" },
    { kind: "yes_no",            label: "Yes / No",          icon: ToggleLeft,     group: "choice" },
    { kind: "radio",             label: "Single-choice",     icon: Circle,         group: "choice" },
    { kind: "checkboxes",        label: "Multiple-choice",   icon: ListChecks,     group: "choice" },
    { kind: "dropdown",          label: "Dropdown",          icon: ChevronsUpDown, group: "choice" },
    { kind: "confirmation",      label: "Required confirmation", icon: CheckCircle2, group: "consent" },
    { kind: "initials",          label: "Initials",          icon: Fingerprint,    group: "consent" },
    { kind: "signature",         label: "Electronic signature", icon: PenTool,     group: "consent" },
    { kind: "auto_timestamp",    label: "Auto date & time",  icon: Clock,          group: "meta" },
];
const KIND_META = Object.fromEntries(KINDS.map((k) => [k.kind, k]));
const IS_LAYOUT = (k) => k === "section_heading" || k === "informational_text";
const NEEDS_OPTIONS = (k) => ["radio", "checkboxes", "dropdown"].includes(k);
const IS_CONDITIONABLE = (k) => ["yes_no", "radio", "checkboxes", "dropdown"].includes(k);

const uid = () => crypto.randomUUID();
const toSnake = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 60) || "field";

/* ==========================================================================
 * FormBuilder (main)
 * ======================================================================= */
export function StaffFormBuilder() {
    const { id } = useParams();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(() => (params.get("tab") === "editor" ? "editor" : "preview"));
    const [device, setDevice] = useState("mobile");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [template, setTemplate] = useState(null);
    const [versions, setVersions] = useState([]);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("other");
    const [sections, setSections] = useState([]);      // working copy of the current version
    const [initialSig, setInitialSig] = useState("");
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const r = await staffApi.getForm(id);
            setTemplate(r.template);
            setVersions(r.versions || []);
            setName(r.template.name || "");
            setCategory(r.template.category || "other");
            // Load draft version content if present, else the published one
            const draftId = r.template.draft_version_id;
            const pubId = r.template.published_version_id;
            const v = (r.versions || []).find((x) => x.id === draftId) || (r.versions || []).find((x) => x.id === pubId);
            const secs = v?.sections || [];
            setSections(secs);
            setInitialSig(JSON.stringify({ name: r.template.name, category: r.template.category, sections: secs }));
        } catch (e) { setError(e); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    // Update URL tab
    useEffect(() => { const p = new URLSearchParams(params); p.set("tab", tab); setParams(p, { replace: true }); /* eslint-disable-next-line */ }, [tab]);

    // Dirty flag + unload guard
    const currentSig = useMemo(() => JSON.stringify({ name, category, sections }), [name, category, sections]);
    const dirty = initialSig && currentSig !== initialSig;
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const goBack = () => {
        if (dirty && !window.confirm("Unsaved changes will be lost. Leave anyway?")) return;
        nav("/staff/forms");
    };

    const saveDraft = async () => {
        // Validate: sections + fields
        for (const s of sections) {
            for (const f of s.fields || []) {
                if (!IS_LAYOUT(f.kind) && !f.label?.trim()) {
                    toast.error(`Field label is required (section "${s.title || "Untitled"}").`);
                    return;
                }
                if (NEEDS_OPTIONS(f.kind) && (!f.options || f.options.filter((o) => o.label?.trim()).length === 0)) {
                    toast.error(`Field "${f.label}" needs at least one option.`);
                    return;
                }
            }
        }
        setBusy(true);
        try {
            if (name !== template.name || category !== template.category) {
                await staffApi.patchForm(id, { name, category });
            }
            const cleanSections = sections.map((s) => ({
                id: s.id, title: s.title || "", description: s.description || "",
                fields: (s.fields || []).map((f) => ({
                    id: f.id, kind: f.kind, label: f.label || "", name: f.name || toSnake(f.label),
                    description: f.description || "", placeholder: f.placeholder || "",
                    required: !!f.required,
                    options: NEEDS_OPTIONS(f.kind) ? (f.options || []).filter((o) => (o.label || "").trim()) : [],
                    conditional: f.conditional || null,
                })),
            }));
            await staffApi.saveFormDraft(id, cleanSections);
            setSavedAt(new Date());
            toast.success("Draft saved");
            await load();
        } catch (e) { toast.error(e?.message || "Save failed"); }
        finally { setBusy(false); }
    };

    const publish = async () => {
        setBusy(true);
        try {
            if (dirty) await saveDraft();
            await staffApi.publishForm(id);
            setPublishConfirmOpen(false);
            toast.success("Published");
            await load();
        } catch (e) { toast.error(e?.message || "Publish failed"); }
        finally { setBusy(false); }
    };

    const saveAsNew = async () => {
        if (dirty && !window.confirm("Save current unsaved changes into a new form? Your unsaved edits will be captured as the starting point.")) return;
        setBusy(true);
        try {
            // If dirty, save current draft first so the snapshot includes the latest edits.
            if (dirty) await saveDraft();
            const r = await staffApi.saveFormAsNew(id, { name: `${name} (copy)`, category });
            toast.success("New form created");
            nav(`/staff/forms/${r.template.id}`);
        } catch (e) { toast.error(e?.message || "Save as new failed"); }
        finally { setBusy(false); }
    };

    const archive = async () => {
        if (!window.confirm(`Archive "${name}"? Historical submissions are preserved and the form is hidden from service assignment. This is reversible.`)) return;
        setBusy(true);
        try {
            await staffApi.archiveForm(id);
            toast.success("Archived");
            await load();
        } catch (e) { toast.error(e?.message || "Archive failed"); }
        finally { setBusy(false); }
    };

    const restore = async () => {
        setBusy(true);
        try {
            await staffApi.restoreForm(id);
            toast.success("Restored");
            await load();
        } catch (e) { toast.error(e?.message || "Restore failed"); }
        finally { setBusy(false); }
    };

    const doDelete = async () => {
        const subs = template?.submissions_count || 0;
        if (subs > 0) { toast.error(`Cannot delete: ${subs} completed customer submission${subs > 1 ? "s" : ""} exist. Archive instead.`); return; }
        if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
        setBusy(true);
        try {
            await staffApi.deleteForm(id);
            toast.success("Deleted");
            nav("/staff/forms");
        } catch (e) { toast.error(e?.message || "Delete failed"); setBusy(false); }
    };

    // Flat list of prior conditionable fields (must be declared before any
    // conditional return to obey the rules-of-hooks).
    const conditionSources = useMemo(() => {
        const out = [];
        for (const s of sections) for (const f of (s.fields || [])) {
            if (IS_CONDITIONABLE(f.kind)) out.push({ ...f, sectionTitle: s.title });
        }
        return out;
    }, [sections]);

    if (!template && !error) return <Skeleton className="h-96" />;
    if (error) return <ErrorState error={error} onRetry={load} />;

    /* ------------- section / field mutations ------------- */
    const addSection = () => setSections((prev) => [...prev, { id: uid(), title: "New section", description: "", fields: [] }]);
    const patchSection = (sid, patch) => setSections((prev) => prev.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
    const removeSection = (sid) => {
        if (!window.confirm("Delete this section and its fields?")) return;
        setSections((prev) => prev.filter((s) => s.id !== sid));
    };
    const moveSection = (sid, dir) => setSections((prev) => {
        const i = prev.findIndex((s) => s.id === sid);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        const next = [...prev]; const [it] = next.splice(i, 1); next.splice(j, 0, it); return next;
    });
    const addField = (sid, kind) => {
        const meta = KIND_META[kind];
        const label = IS_LAYOUT(kind) ? "" : meta.label;
        const opts = NEEDS_OPTIONS(kind) ? [{ id: uid(), label: "Option 1" }, { id: uid(), label: "Option 2" }] : [];
        const f = { id: uid(), kind, label, name: toSnake(label || meta.label), description: "", placeholder: "", required: false, options: opts, conditional: null };
        patchSection(sid, { fields: [...(sections.find((s) => s.id === sid)?.fields || []), f] });
    };
    const patchField = (sid, fid, patch) => setSections((prev) => prev.map((s) => s.id !== sid ? s : { ...s, fields: s.fields.map((f) => f.id === fid ? { ...f, ...patch } : f) }));
    const removeField = (sid, fid) => setSections((prev) => prev.map((s) => s.id !== sid ? s : { ...s, fields: s.fields.filter((f) => f.id !== fid) }));
    const moveField = (sid, fid, dir) => setSections((prev) => prev.map((s) => {
        if (s.id !== sid) return s;
        const i = s.fields.findIndex((f) => f.id === fid);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= s.fields.length) return s;
        const next = [...s.fields]; const [it] = next.splice(i, 1); next.splice(j, 0, it);
        return { ...s, fields: next };
    }));

    return (
        <div className="space-y-5" data-testid="form-builder">
            {template.status === "archived" && (
                <div className="rounded-2xl border hairline bg-red-500/5 border-red-400/30 p-4 flex items-start gap-3" data-testid="fb-archived-banner">
                    <Archive className="h-4 w-4 text-red-300 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1">
                        <p className="text-sm text-red-100">This form is archived. Read only.</p>
                        <p className="text-[11px] text-white/50 mt-0.5">Historical submissions are preserved. Restore to edit again.</p>
                    </div>
                    <button onClick={restore} disabled={busy} data-testid="fb-restore"
                            className="h-9 px-4 rounded-full border hairline text-white/90 hover:text-gold hover:border-gold/60 text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1 disabled:opacity-40">
                        <RotateCcw className="h-3 w-3" /> Restore
                    </button>
                </div>
            )}
            {template.status !== "archived" && template.seed_key && (
                <div className="rounded-xl border hairline bg-gold/[0.04] border-gold/30 p-3 flex items-start gap-2" data-testid="fb-seed-banner">
                    <Info className="h-4 w-4 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                    <p className="text-[11px] text-white/70 leading-relaxed">
                        This is a <span className="text-gold">standard template</span> we've included as a starting point. Edit any wording, add new fields, or use <span className="text-white">Save as new version</span> to keep the original intact.
                    </p>
                </div>
            )}

            {/* Header: title row + action row. Wraps freely on mobile so nothing overflows. */}
            <div className="space-y-3">
                <div className="flex items-start gap-3">
                    <button onClick={goBack} className="min-h-10 min-w-10 rounded-full border hairline text-white/70 hover:text-gold flex items-center justify-center shrink-0" data-testid="fb-back">
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div className="min-w-0 flex-1">
                        {tab === "preview" ? (
                            <h1
                                data-testid="fb-name"
                                className="font-serif text-white text-lg sm:text-xl md:text-2xl lg:text-3xl leading-tight break-words py-1"
                            >
                                {name || "Untitled form"}
                            </h1>
                        ) : (
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={template.status === "archived"}
                                data-testid="fb-name"
                                className="w-full bg-transparent font-serif text-white text-lg sm:text-xl md:text-2xl lg:text-3xl focus:outline-none border-b border-transparent focus:border-gold/40 py-1 disabled:opacity-90 leading-tight"
                            />
                        )}
                        <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-white/45 flex-wrap">
                            {tab === "editor" ? (
                                <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="fb-category"
                                        disabled={template.status === "archived"}
                                        className="bg-navy border hairline rounded-md text-[10px] uppercase tracking-[0.14em] text-white/70 px-2 py-1">
                                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            ) : (
                                <span className="bg-white/[0.06] border hairline rounded-md text-[10px] uppercase tracking-[0.14em] text-white/70 px-2 py-1">
                                    {(CATEGORIES.find((c) => c.value === category) || {}).label || category}
                                </span>
                            )}
                            <span className={`inline-flex text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${template.status === "published" ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : template.status === "archived" ? "bg-red-500/10 border-red-400/30 text-red-200" : "bg-white/10 border-white/25 text-white/80"}`} data-testid="fb-status">
                                {template.status}
                            </span>
                            {(template.submissions_count > 0) && (
                                <span className="text-gold/90">· {template.submissions_count} completed</span>
                            )}
                            {dirty && <span className="text-gold">· unsaved</span>}
                            {savedAt && !dirty && <span className="text-emerald-300">· saved {savedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}
                        </div>
                    </div>
                </div>

                {/* Action row — always shows Preview | Edit Form toggle; wraps freely on mobile */}
                <div className="flex items-center gap-2 flex-wrap">
                    {template.status !== "archived" && (
                        <div className="flex rounded-full border hairline overflow-hidden" role="tablist" data-testid="fb-mode-tabs">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "preview"}
                                onClick={() => setTab("preview")}
                                data-testid="fb-tab-preview"
                                className={`px-4 h-10 text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1 ${tab === "preview" ? "bg-gold text-navy font-semibold" : "text-white/75 hover:text-gold"}`}
                            >
                                <Eye className="h-3 w-3" /> Preview
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "editor"}
                                onClick={() => setTab("editor")}
                                data-testid="fb-tab-editor"
                                className={`px-4 h-10 text-xs uppercase tracking-[0.14em] inline-flex items-center gap-1 border-l hairline ${tab === "editor" ? "bg-gold text-navy font-semibold" : "text-white/75 hover:text-gold"}`}
                            >
                                <Pencil className="h-3 w-3" /> Edit form
                            </button>
                        </div>
                    )}
                    {template.status !== "archived" && tab === "editor" && (
                        <>
                            <button onClick={saveDraft} disabled={busy || !dirty} data-testid="fb-save"
                                    className="h-10 px-4 rounded-full border hairline text-xs uppercase tracking-[0.14em] text-white/80 hover:text-gold hover:border-gold/60 disabled:opacity-40 inline-flex items-center gap-1">
                                <Save className="h-3 w-3" /> Save
                            </button>
                            <button onClick={() => setPublishConfirmOpen(true)} disabled={busy || sections.length === 0} data-testid="fb-publish"
                                    className="h-10 px-4 rounded-full bg-gold text-navy font-semibold text-xs uppercase tracking-[0.14em] disabled:opacity-40 inline-flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" /> {template.status === "published" ? "Republish" : "Publish"}
                            </button>
                            <MoreMenu
                                onSaveAsNew={saveAsNew}
                                onArchive={archive}
                                onDelete={doDelete}
                                canDelete={(template.submissions_count || 0) === 0}
                                busy={busy}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Body */}
            {tab === "editor" ? (
                <EditorPane
                    sections={sections}
                    conditionSources={conditionSources}
                    onAddSection={addSection}
                    onPatchSection={patchSection}
                    onRemoveSection={removeSection}
                    onMoveSection={moveSection}
                    onAddField={addField}
                    onPatchField={patchField}
                    onRemoveField={removeField}
                    onMoveField={moveField}
                />
            ) : (
                <PreviewPane device={device} setDevice={setDevice} name={name} sections={sections} />
            )}

            {/* Versions: hidden by default, revealed only when explicitly requested */}
            <VersionHistoryPanel versions={versions} />

            {/* Publish confirmation */}
            {publishConfirmOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setPublishConfirmOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5 space-y-3" data-testid="fb-publish-modal">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-1" />
                            <div>
                                <h3 className="font-serif text-white text-xl">
                                    {template.status === "published" ? "Publish a new version?" : "Publish this form?"}
                                </h3>
                                <p className="text-sm text-white/70 mt-2">
                                    {template.status === "published"
                                        ? "This will create a new published version. Every previous version and all customer submissions against it remain untouched. Nothing historical is lost."
                                        : "Once published, future substantive changes will create a new form version. Previous versions are retained forever so any customer submissions stay tied to the exact form they signed."}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setPublishConfirmOpen(false)} className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                            <button onClick={publish} disabled={busy} data-testid="fb-publish-confirm"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm">
                                {template.status === "published" ? "Publish v" + ((template.latest_version_number || 1)) : "Publish"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ==========================================================================
 * More menu (Save As New, Archive, Delete)
 * ======================================================================= */
function MoreMenu({ onSaveAsNew, onArchive, onDelete, canDelete, busy }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);
    return (
        <div ref={ref} className="relative">
            <button onClick={() => setOpen((v) => !v)} disabled={busy} data-testid="fb-more"
                    className="h-10 w-10 rounded-full border hairline text-white/80 hover:text-gold hover:border-gold/60 inline-flex items-center justify-center disabled:opacity-40">
                <MoreVertical className="h-4 w-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-12 z-40 rounded-xl border hairline bg-navy shadow-xl overflow-hidden min-w-[220px]" data-testid="fb-more-menu">
                    <button onClick={() => { setOpen(false); onSaveAsNew(); }}
                            className="w-full h-11 px-4 text-left text-sm text-white/85 hover:bg-gold/10 hover:text-gold inline-flex items-center gap-2"
                            data-testid="fb-save-as-new">
                        <GitBranch className="h-3.5 w-3.5" /> Save as new form
                    </button>
                    <button onClick={() => { setOpen(false); onArchive(); }}
                            className="w-full h-11 px-4 text-left text-sm text-white/85 hover:bg-red-500/10 hover:text-red-200 inline-flex items-center gap-2 border-t hairline"
                            data-testid="fb-archive">
                        <Archive className="h-3.5 w-3.5" /> Archive form
                    </button>
                    {canDelete && (
                        <button onClick={() => { setOpen(false); onDelete(); }}
                                className="w-full h-11 px-4 text-left text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 inline-flex items-center gap-2 border-t hairline"
                                data-testid="fb-delete">
                            <Trash2 className="h-3.5 w-3.5" /> Delete permanently
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/* ==========================================================================
 * Editor pane
 * ======================================================================= */
function EditorPane({ sections, conditionSources, onAddSection, onPatchSection, onRemoveSection, onMoveSection, onAddField, onPatchField, onRemoveField, onMoveField }) {
    return (
        <div className="space-y-3" data-testid="fb-editor">
            {sections.length === 0 && (
                <div className="rounded-2xl border hairline p-8 text-center text-white/60">
                    <p className="font-serif text-white text-xl mb-1">No sections yet</p>
                    <p className="text-sm">Add a section, then add fields to it.</p>
                </div>
            )}
            {sections.map((s, idx) => (
                <SectionEditor
                    key={s.id}
                    section={s}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < sections.length - 1}
                    onPatch={(p) => onPatchSection(s.id, p)}
                    onRemove={() => onRemoveSection(s.id)}
                    onMoveUp={() => onMoveSection(s.id, -1)}
                    onMoveDown={() => onMoveSection(s.id, 1)}
                    onAddField={(kind) => onAddField(s.id, kind)}
                    onPatchField={(fid, p) => onPatchField(s.id, fid, p)}
                    onRemoveField={(fid) => onRemoveField(s.id, fid)}
                    onMoveField={(fid, dir) => onMoveField(s.id, fid, dir)}
                    conditionSources={conditionSources.filter((c) => (s.fields || []).findIndex((x) => x.id === c.id) === -1 || true)}
                />
            ))}
            <button onClick={onAddSection} data-testid="fb-add-section"
                    className="w-full h-12 rounded-2xl border-2 border-dashed border-white/15 text-white/60 hover:text-gold hover:border-gold/40 inline-flex items-center justify-center gap-2 text-sm">
                <Plus className="h-4 w-4" /> Add section
            </button>
        </div>
    );
}

function SectionEditor({ section, canMoveUp, canMoveDown, onPatch, onRemove, onMoveUp, onMoveDown, onAddField, onPatchField, onRemoveField, onMoveField, conditionSources }) {
    const [addOpen, setAddOpen] = useState(false);
    return (
        <div className="rounded-2xl border hairline bg-white/[0.02] p-4 space-y-3" data-testid={`fb-section-${section.id}`}>
            <div className="flex items-start gap-2">
                <GripVertical className="h-4 w-4 text-white/30 mt-2 shrink-0" />
                <div className="flex-1 min-w-0 space-y-1">
                    <input value={section.title} onChange={(e) => onPatch({ title: e.target.value })}
                           placeholder="Section title" data-testid="fb-section-title"
                           className="w-full bg-transparent text-white font-serif text-xl focus:outline-none border-b border-transparent focus:border-gold/40 py-1" />
                    <textarea value={section.description || ""} onChange={(e) => onPatch({ description: e.target.value })}
                              rows={1} placeholder="Optional guidance shown to the customer"
                              className="w-full bg-transparent text-white/60 text-xs focus:outline-none resize-none" />
                </div>
                <div className="flex flex-col gap-1">
                    <button onClick={onMoveUp} disabled={!canMoveUp} className="h-8 w-8 rounded-full border hairline text-white/60 hover:text-gold disabled:opacity-30 inline-flex items-center justify-center" data-testid="fb-section-up"><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={onMoveDown} disabled={!canMoveDown} className="h-8 w-8 rounded-full border hairline text-white/60 hover:text-gold disabled:opacity-30 inline-flex items-center justify-center" data-testid="fb-section-down"><ChevronDown className="h-3 w-3" /></button>
                    <button onClick={onRemove} className="h-8 w-8 rounded-full border hairline text-red-300/70 hover:border-red-300/50 inline-flex items-center justify-center" data-testid="fb-section-remove"><Trash2 className="h-3 w-3" /></button>
                </div>
            </div>

            {(section.fields || []).length === 0 && (
                <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-white/40 text-xs">No fields in this section yet.</div>
            )}
            {(section.fields || []).map((f, idx) => (
                <FieldEditor
                    key={f.id}
                    field={f}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < section.fields.length - 1}
                    onPatch={(p) => onPatchField(f.id, p)}
                    onRemove={() => onRemoveField(f.id)}
                    onMoveUp={() => onMoveField(f.id, -1)}
                    onMoveDown={() => onMoveField(f.id, 1)}
                    conditionSources={conditionSources.filter((c) => c.id !== f.id)}
                />
            ))}

            {/* Add field */}
            <div>
                <button onClick={() => setAddOpen((v) => !v)} data-testid={`fb-add-field-${section.id}`}
                        className="h-10 px-4 rounded-full border hairline text-xs uppercase tracking-[0.14em] text-white/70 hover:text-gold hover:border-gold/60 inline-flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Add field
                </button>
                {addOpen && (
                    <div className="mt-2 rounded-xl border hairline p-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                        {KINDS.map((k) => {
                            const Icon = k.icon;
                            return (
                                <button key={k.kind} onClick={() => { onAddField(k.kind); setAddOpen(false); }}
                                        data-testid={`fb-kind-${k.kind}`}
                                        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border hairline text-xs text-white/80 hover:text-gold hover:border-gold/50 text-left">
                                    <Icon className="h-3 w-3 text-gold shrink-0" /><span className="truncate">{k.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function FieldEditor({ field, canMoveUp, canMoveDown, onPatch, onRemove, onMoveUp, onMoveDown, conditionSources }) {
    const [open, setOpen] = useState(false);
    const meta = KIND_META[field.kind] || {};
    const Icon = meta.icon || Type;
    return (
        <div className="rounded-xl border hairline bg-white/[0.03] p-3 space-y-2" data-testid={`fb-field-${field.id}`}>
            <div className="flex items-start gap-2 min-w-0">
                <Icon className="h-4 w-4 text-gold mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0 space-y-1">
                    <input value={field.label} onChange={(e) => onPatch({ label: e.target.value, name: field.name || toSnake(e.target.value) })}
                           placeholder={IS_LAYOUT(field.kind) ? (field.kind === "section_heading" ? "Heading text" : "Informational text") : "Field label"}
                           className="w-full bg-transparent text-white text-sm focus:outline-none border-b border-transparent focus:border-gold/40 py-1"
                           data-testid="fb-field-label" />
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-white/40 flex-wrap">
                        <span>{meta.label || field.kind}</span>
                        {field.required && <span className="text-gold">Required</span>}
                        {field.conditional && <span className="text-amber-300">Conditional</span>}
                        <button onClick={() => setOpen((v) => !v)} className="ml-auto text-white/60 hover:text-gold" data-testid="fb-field-toggle">
                            {open ? "Hide settings" : "Settings"}
                        </button>
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <button onClick={onMoveUp} disabled={!canMoveUp} className="h-7 w-7 rounded-full border hairline text-white/60 hover:text-gold disabled:opacity-30 inline-flex items-center justify-center" data-testid="fb-field-up"><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={onMoveDown} disabled={!canMoveDown} className="h-7 w-7 rounded-full border hairline text-white/60 hover:text-gold disabled:opacity-30 inline-flex items-center justify-center" data-testid="fb-field-down"><ChevronDown className="h-3 w-3" /></button>
                    <button onClick={onRemove} className="h-7 w-7 rounded-full border hairline text-red-300/70 hover:border-red-300/50 inline-flex items-center justify-center" data-testid="fb-field-remove"><Trash2 className="h-3 w-3" /></button>
                </div>
            </div>

            {open && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/40 md:col-span-2">Description / help text
                        <textarea value={field.description || ""} onChange={(e) => onPatch({ description: e.target.value })}
                                  rows={2} className="mt-1 w-full bg-transparent border hairline rounded-md p-2 text-xs text-white focus:outline-none focus:border-gold/40" />
                    </label>
                    {!IS_LAYOUT(field.kind) && (
                        <label className="inline-flex items-center gap-2 text-xs text-white/80 md:col-span-2">
                            <input type="checkbox" checked={!!field.required} onChange={(e) => onPatch({ required: e.target.checked })}
                                   data-testid="fb-field-required" className="accent-gold h-4 w-4" /> Required
                        </label>
                    )}
                    {NEEDS_OPTIONS(field.kind) && (
                        <OptionsEditor options={field.options || []} onChange={(opts) => onPatch({ options: opts })} />
                    )}
                    {!IS_LAYOUT(field.kind) && (
                        <ConditionalEditor field={field} sources={conditionSources} onChange={(rule) => onPatch({ conditional: rule })} />
                    )}
                </div>
            )}
        </div>
    );
}

function OptionsEditor({ options, onChange }) {
    const set = (idx, patch) => onChange(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
    const add = () => onChange([...options, { id: uid(), label: `Option ${options.length + 1}` }]);
    const remove = (idx) => onChange(options.filter((_, i) => i !== idx));
    const move = (idx, dir) => {
        const j = idx + dir; if (j < 0 || j >= options.length) return;
        const next = [...options]; const [it] = next.splice(idx, 1); next.splice(j, 0, it); onChange(next);
    };
    return (
        <div className="md:col-span-2 rounded-lg border hairline p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">Choice options</div>
            {options.map((o, idx) => (
                <div key={o.id} className="flex items-center gap-2">
                    <input value={o.label} onChange={(e) => set(idx, { label: e.target.value })}
                           className="flex-1 min-w-0 bg-transparent border hairline rounded-md p-2 text-xs text-white" data-testid={`fb-opt-${o.id}`} />
                    <button onClick={() => move(idx, -1)} className="h-8 w-8 rounded-full border hairline text-white/60 disabled:opacity-30 inline-flex items-center justify-center" disabled={idx === 0}><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={() => move(idx, 1)} className="h-8 w-8 rounded-full border hairline text-white/60 disabled:opacity-30 inline-flex items-center justify-center" disabled={idx === options.length - 1}><ChevronDown className="h-3 w-3" /></button>
                    <button onClick={() => remove(idx)} className="h-8 w-8 rounded-full border hairline text-red-300/70 inline-flex items-center justify-center"><Trash2 className="h-3 w-3" /></button>
                </div>
            ))}
            <button onClick={add} data-testid="fb-opt-add" className="text-xs text-gold inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add option</button>
        </div>
    );
}

function ConditionalEditor({ field, sources, onChange }) {
    const rule = field.conditional || null;
    const [enabled, setEnabled] = useState(!!rule);
    useEffect(() => { setEnabled(!!rule); }, [rule]);
    const src = sources.find((s) => s.id === rule?.field_id) || null;

    const setSource = (id) => {
        const s = sources.find((x) => x.id === id);
        if (!s) return;
        const defaultVal = s.kind === "yes_no" ? "yes" : (s.options?.[0]?.label || "");
        onChange({ field_id: s.id, operator: s.kind === "checkboxes" ? "in" : "eq", value: defaultVal });
    };

    return (
        <div className="md:col-span-2 rounded-lg border hairline p-3 space-y-2">
            <label className="inline-flex items-center gap-2 text-xs text-white/80">
                <input type="checkbox" checked={enabled} onChange={(e) => { setEnabled(e.target.checked); if (!e.target.checked) onChange(null); }}
                       data-testid="fb-cond-enable" className="accent-gold h-4 w-4" />
                Only show this field when another answer matches
            </label>
            {enabled && (
                sources.length === 0 ? (
                    <p className="text-[11px] text-white/50">Add a Yes/No, radio, checkbox or dropdown field earlier in the form to base a condition on.</p>
                ) : (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-white/80">
                        <span>When</span>
                        <select value={rule?.field_id || ""} onChange={(e) => setSource(e.target.value)} data-testid="fb-cond-field"
                                className="bg-navy border hairline rounded-md text-xs px-2 py-1">
                            <option value="">Select a field…</option>
                            {sources.map((s) => <option key={s.id} value={s.id}>{s.label || s.name}</option>)}
                        </select>
                        {src && (
                            <>
                                <span>is</span>
                                {src.kind === "yes_no" ? (
                                    <select value={rule?.value ?? "yes"} onChange={(e) => onChange({ ...rule, value: e.target.value })}
                                            data-testid="fb-cond-value"
                                            className="bg-navy border hairline rounded-md text-xs px-2 py-1">
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                ) : (
                                    <select value={rule?.value ?? ""} onChange={(e) => onChange({ ...rule, value: e.target.value })}
                                            data-testid="fb-cond-value"
                                            className="bg-navy border hairline rounded-md text-xs px-2 py-1">
                                        <option value="">…</option>
                                        {(src.options || []).map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
                                    </select>
                                )}
                            </>
                        )}
                    </div>
                )
            )}
        </div>
    );
}

/* ==========================================================================
 * Preview pane — white panel, navy text, natural spacing (matches customer form)
 * ======================================================================= */
function PreviewPane({ device, setDevice, name, sections }) {
    const width = device === "mobile" ? 390 : device === "tablet" ? 720 : 1080;
    const [answers, setAnswers] = useState({});
    const setAnswer = (id, v) => setAnswers((prev) => ({ ...prev, [id]: v }));

    const isVisible = (f) => {
        const rule = f.conditional;
        if (!rule) return true;
        const target = Object.entries(answers).find(([id]) => id === rule.field_id);
        const value = target ? target[1] : undefined;
        if (rule.operator === "eq") return String(value ?? "") === String(rule.value ?? "");
        if (rule.operator === "in") return Array.isArray(value) && value.includes(rule.value);
        return true;
    };

    return (
        <div className="space-y-3" data-testid="fb-preview">
            <div className="flex items-center gap-2 flex-wrap">
                {[["mobile","Mobile"],["tablet","Tablet"],["desktop","Desktop"]].map(([k, l]) => (
                    <button key={k} onClick={() => setDevice(k)} data-testid={`fb-device-${k}`}
                            className={`h-9 px-3 rounded-full border text-xs uppercase tracking-[0.14em] ${device === k ? "bg-gold text-navy border-gold" : "hairline text-white/70 hover:border-gold/60"}`}>
                        {l}
                    </button>
                ))}
            </div>
            <div className="w-full flex justify-center">
                <div
                    className="rounded-2xl bg-white text-navy shadow-lg p-5 sm:p-6"
                    style={{ width: "100%", maxWidth: width }}
                    data-testid="fb-preview-panel"
                >
                    <h2 className="font-serif text-navy text-2xl mb-1">{name || "Untitled form"}</h2>
                    <p className="text-xs text-slate-500 mb-5">Preview. This is exactly what your customer will see.</p>
                    {sections.length === 0 && <p className="text-slate-500 text-sm">Add sections and fields to preview them.</p>}
                    <div className="space-y-6">
                        {sections.map((s) => (
                            <section key={s.id} className="space-y-3">
                                {s.title && <h3 className="font-serif text-navy text-lg leading-tight">{s.title}</h3>}
                                {s.description && <p className="text-slate-500 text-xs leading-relaxed">{s.description}</p>}
                                <div className="space-y-4">
                                    {(s.fields || []).map((f) => isVisible(f) && (
                                        <PreviewField key={f.id} field={f} value={answers[f.id]} onChange={(v) => setAnswer(f.id, v)} />
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PreviewField({ field, value, onChange }) {
    const input = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-navy placeholder-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30";
    const labelText = field.label || "";
    const label = (
        <label className="block text-sm text-navy font-medium mb-1.5">
            {labelText}
            {field.required && <span className="text-gold ml-1" aria-label="required">*</span>}
        </label>
    );
    const help = field.description ? <p className="text-xs text-slate-500 mt-1.5">{field.description}</p> : null;

    switch (field.kind) {
        case "section_heading":
            return <h4 className="font-serif text-navy text-lg mt-1">{labelText}</h4>;
        case "informational_text":
            return <p className="text-sm text-slate-600 leading-relaxed">{field.description || labelText}</p>;
        case "short_text":
            return <div>{label}<input placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "long_text":
            return <div>{label}<textarea rows={3} placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "email":
            return <div>{label}<input type="email" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "phone":
            return <div>{label}<input type="tel" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "date":
        case "date_of_birth":
            return <div>{label}<input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "number":
            return <div>{label}<input type="number" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`} />{help}</div>;
        case "yes_no":
            return (
                <div>{label}
                    <div className="flex gap-2">
                        {["yes", "no"].map((v) => (
                            <button key={v} type="button" onClick={() => onChange(v)} data-testid={`pv-${field.id}-${v}`}
                                    className={`h-10 px-5 rounded-full border text-sm ${value === v ? "bg-gold text-navy border-gold font-semibold" : "border-slate-300 text-navy hover:border-gold/60"}`}>
                                {v === "yes" ? "Yes" : "No"}
                            </button>
                        ))}
                    </div>
                    {help}
                </div>
            );
        case "radio":
            return (
                <div>{label}
                    <div className="space-y-2">
                        {(field.options || []).map((o) => (
                            <label key={o.id} className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                                <input type="radio" name={field.id} checked={value === o.label} onChange={() => onChange(o.label)} className="accent-gold h-4 w-4" data-testid={`pv-${field.id}-${o.id}`} />
                                <span>{o.label}</span>
                            </label>
                        ))}
                    </div>
                    {help}
                </div>
            );
        case "checkboxes": {
            const arr = Array.isArray(value) ? value : [];
            const toggle = (l) => onChange(arr.includes(l) ? arr.filter((x) => x !== l) : [...arr, l]);
            return (
                <div>{label}
                    <div className="space-y-2">
                        {(field.options || []).map((o) => (
                            <label key={o.id} className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                                <input type="checkbox" checked={arr.includes(o.label)} onChange={() => toggle(o.label)} className="accent-gold h-4 w-4" data-testid={`pv-${field.id}-${o.id}`} />
                                <span>{o.label}</span>
                            </label>
                        ))}
                    </div>
                    {help}
                </div>
            );
        }
        case "dropdown":
            return (
                <div>{label}
                    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} data-testid={`pv-${field.id}`}>
                        <option value="">{field.placeholder || "Select…"}</option>
                        {(field.options || []).map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
                    </select>
                    {help}
                </div>
            );
        case "confirmation":
            return (
                <label className="flex items-start gap-2 text-sm text-navy cursor-pointer">
                    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-gold mt-1 h-4 w-4" data-testid={`pv-${field.id}`} />
                    <span>
                        {labelText}
                        {field.required && <span className="text-gold ml-1" aria-label="required">*</span>}
                    </span>
                </label>
            );
        case "initials":
            return <div>{label}<input maxLength={4} placeholder="e.g. AT" value={value || ""} onChange={(e) => onChange(e.target.value.toUpperCase())} className={`${input} w-24 tracking-widest uppercase`} data-testid={`pv-${field.id}`} />{help}</div>;
        case "signature":
            return <div>{label}<div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-500 text-xs italic">Signature will be captured here on the customer form</div>{help}</div>;
        case "auto_timestamp":
            return <p className="text-xs text-slate-500">Auto-recorded when the customer submits the form.</p>;
        default:
            return null;
    }
}

/* ==========================================================================
 * Version history
 * ======================================================================= */
/* ==========================================================================
 * Version history — collapsed by default, toggled by the "View version history" button
 * ======================================================================= */
function VersionHistoryPanel({ versions }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid="fb-versions-panel">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="fb-versions-toggle"
                className="h-10 px-4 rounded-full border hairline text-xs uppercase tracking-[0.14em] text-navy/70 hover:text-gold hover:border-gold/60 inline-flex items-center gap-1 bg-white/70 shadow-sm"
            >
                {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {open ? "Hide version history" : "View version history"}
            </button>
            {open && (
                <div className="mt-3">
                    <VersionHistory versions={versions} />
                </div>
            )}
        </div>
    );
}

function VersionHistory({ versions }) {
    return (
        <div className="rounded-2xl border hairline p-4 bg-white/[0.02]" data-testid="fb-versions">
            <h3 className="font-serif text-white text-lg mb-2">Version history</h3>
            {versions.length === 0 ? (
                <p className="text-white/50 text-sm">No versions yet.</p>
            ) : (
                <ul className="divide-y divide-white/5">
                    {versions.map((v) => (
                        <li key={v.id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`fb-version-${v.version_number}`}>
                            <div className="min-w-0">
                                <span className="text-white">v{v.version_number}</span>
                                <span className="text-white/50 text-xs ml-2">
                                    {v.published_at
                                        ? `Published ${new Date(v.published_at).toLocaleString("en-GB")}`
                                        : `Draft · updated ${new Date(v.updated_at || v.created_at).toLocaleString("en-GB")}`}
                                </span>
                            </div>
                            <span className={`inline-flex text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${v.status === "published" ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : "bg-white/10 border-white/25 text-white/80"}`}>
                                {v.status}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default StaffFormBuilder;
